import { DAY_NAMES, formatDate } from './utils';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function rowsForWeek(weekKey, weekData) {
  const rows = [];
  DAY_NAMES.forEach(dayName => {
    const day = weekData.days?.[dayName];
    if (!day) return;
    let section = '';
    (day.tasks || []).forEach(task => {
      if (task.type === 'tag') {
        section = task.text || 'Section';
        return;
      }

      const hasTask = String(task.text || '').trim();
      const hasNote = String(task.comment || '').trim();
      const hasSubs = (task.subtasks || []).some(sub => String(sub.text || '').trim());
      const hours = Number(task.hours || 0);
      if (!hasTask && !hasNote && !hasSubs && !hours) return;

      rows.push({
        weekKey,
        date: formatDate(day.date),
        dayName,
        section,
        task: task.text || '',
        status: task.done ? 'Done' : 'Open',
        hours,
        note: task.comment || '',
        subtasks: (task.subtasks || [])
          .filter(sub => String(sub.text || '').trim())
          .map(sub => `${sub.done ? '[Done]' : '[Open]'} ${sub.text}`)
          .join('\n'),
      });
    });
  });
  return rows;
}

export function buildReportText(selectedWeeks, allData) {
  const lines = ['Weekly Planner report'];
  selectedWeeks.forEach(week => {
    const weekData = allData[week.key];
    if (!weekData) return;
    const rows = rowsForWeek(week.key, weekData);
    const totalHours = rows.reduce((sum, row) => sum + Number(row.hours || 0), 0);
    lines.push('', `${week.label} | ${totalHours} hours`);
    rows.forEach(row => {
      lines.push(`- ${row.dayName} | ${row.section || 'General'} | ${row.task || '(no title)'} | ${row.hours}h | ${row.status}`);
      if (row.note) lines.push(`  Note: ${row.note}`);
      if (row.subtasks) lines.push(`  Subtasks: ${row.subtasks.replace(/\n/g, '; ')}`);
    });
  });
  return lines.join('\n');
}

export function exportWeeksToExcel(selectedWeeks, allData, userEmail) {
  const generatedAt = new Date().toLocaleString('en-IN');
  const allRows = selectedWeeks.flatMap(week => {
    const weekData = allData[week.key];
    return weekData ? rowsForWeek(week.key, weekData) : [];
  });
  const totalHours = allRows.reduce((sum, row) => sum + Number(row.hours || 0), 0);

  const tableRows = allRows.map(row => `
    <tr>
      <td>${escapeHtml(row.weekKey)}</td>
      <td>${escapeHtml(row.date)}</td>
      <td>${escapeHtml(row.dayName)}</td>
      <td>${escapeHtml(row.section)}</td>
      <td>${escapeHtml(row.task)}</td>
      <td>${escapeHtml(row.status)}</td>
      <td class="num">${escapeHtml(row.hours)}</td>
      <td>${escapeHtml(row.note)}</td>
      <td class="wrap">${escapeHtml(row.subtasks).replace(/\n/g, '<br/>')}</td>
    </tr>
  `).join('');

  const weekSummaryRows = selectedWeeks.map(week => {
    const rows = allData[week.key] ? rowsForWeek(week.key, allData[week.key]) : [];
    const weekHours = rows.reduce((sum, row) => sum + Number(row.hours || 0), 0);
    const done = rows.filter(row => row.status === 'Done').length;
    return `
      <tr>
        <td>${escapeHtml(week.key)}</td>
        <td>${escapeHtml(week.label)}</td>
        <td class="num">${rows.length}</td>
        <td class="num">${done}</td>
        <td class="num">${weekHours}</td>
      </tr>
    `;
  }).join('');

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          body { font-family: Arial, sans-serif; color: #172033; }
          h1 { color: #6d35ea; }
          h2 { color: #ff7b23; margin-top: 24px; }
          table { border-collapse: collapse; width: 100%; }
          th { background: #6d35ea; color: #fff; font-weight: 700; }
          th, td { border: 1px solid #d8deea; padding: 8px; vertical-align: top; }
          tr:nth-child(even) { background: #f7f9ff; }
          .meta td { background: #eef7ff; font-weight: 700; }
          .num { text-align: right; }
          .wrap { mso-data-placement:same-cell; white-space: normal; }
        </style>
      </head>
      <body>
        <h1>Weekly Planner Export</h1>
        <table class="meta">
          <tr><td>User</td><td>${escapeHtml(userEmail)}</td></tr>
          <tr><td>Generated</td><td>${escapeHtml(generatedAt)}</td></tr>
          <tr><td>Selected weeks</td><td>${escapeHtml(selectedWeeks.length)}</td></tr>
          <tr><td>Total hours</td><td>${escapeHtml(totalHours)}</td></tr>
        </table>

        <h2>Week Summary</h2>
        <table>
          <thead><tr><th>Week</th><th>Range</th><th>Tasks</th><th>Done</th><th>Hours</th></tr></thead>
          <tbody>${weekSummaryRows || '<tr><td colspan="5">No filled tasks found.</td></tr>'}</tbody>
        </table>

        <h2>Task Detail</h2>
        <table>
          <thead>
            <tr>
              <th>Week</th><th>Date</th><th>Day</th><th>Tag</th><th>Task</th>
              <th>Status</th><th>Hours</th><th>Note</th><th>Subtasks</th>
            </tr>
          </thead>
          <tbody>${tableRows || '<tr><td colspan="9">No filled tasks found.</td></tr>'}</tbody>
        </table>
      </body>
    </html>
  `;

  const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `weekly-planner-export-${new Date().toISOString().slice(0, 10)}.xls`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
